import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Modal as AntdModal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Tag,
  InputNumber,
  Typography,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  PercentageOutlined,
  StopOutlined,
  PlayCircleOutlined,
  CrownOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { registrarService } from '../../services/registrarService';
import { Alert } from 'antd';
import GiftRulesModal from '../../components/domain/GiftRulesModal';

const { Title, Text } = Typography;

export default function TldPricing() {
  const [loading, setLoading] = useState(false);
  const [tldPricings, setTldPricings] = useState([]);
  const [registrars, setRegistrars] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTld, setEditingTld] = useState(null);
  const [filters, setFilters] = useState({});
  const [idnLanguageOptions, setIdnLanguageOptions] = useState([]);
  const [form] = Form.useForm();
  const isIdn = Form.useWatch('is_idn', form);
  const [modeLoadingId, setModeLoadingId] = useState(null);
  const [reenableLoadingId, setReenableLoadingId] = useState(null);
  const [metaVisible, setMetaVisible] = useState(false);
  const [metaContent, setMetaContent] = useState(null);
  const [csvGenerating, setCsvGenerating] = useState(false);
  const [exclusiveLoadingId, setExclusiveLoadingId] = useState(null);
  const [exclusiveModalOpen, setExclusiveModalOpen] = useState(false);
  const [exclusiveMode, setExclusiveMode] = useState('single'); // single | bulk
  const [exclusiveRecord, setExclusiveRecord] = useState(null);
  const [preferredRegistrarIds, setPreferredRegistrarIds] = useState([]);
  const [priorityRules, setPriorityRules] = useState([
    'preferred_registrar',
    'price_lowest',
    'dnssec',
    'privacy',
  ]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [idnFilter, setIdnFilter] = useState(null);
  const [idnScriptFilter, setIdnScriptFilter] = useState(undefined);
  const [giftRow, setGiftRow] = useState(null); // 注册赠品（清单 §4）
  const [multiEnabledFilter, setMultiEnabledFilter] = useState(null);
  // Multi-row selection + bulk markup / enable
  const [selectedTldRowKeys, setSelectedTldRowKeys] = useState([]);
  const [bulkMarkupOpen, setBulkMarkupOpen] = useState(false);
  const [bulkMarkupValue, setBulkMarkupValue] = useState(20);
  const [bulkMarkupLoading, setBulkMarkupLoading] = useState(false);
  const [bulkEnableLoading, setBulkEnableLoading] = useState(false);
  const [hasPricingFilter, setHasPricingFilter] = useState(undefined);
  // 服务端分页（全量 3000+ 行 2.4MB 会让浏览器中途断流）
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  // 跨页保留的已选行
  const [selectedTldRows, setSelectedTldRows] = useState([]);
  const [exporting, setExporting] = useState(false);

  const idnOptions = useMemo(
    () =>
      (idnLanguageOptions || []).map((group) => {
        const labelParts = [group.name_en, group.name_zh].filter(Boolean).join(' / ') || group.name_en || group.code;
        const codesLabel = (group.codes || []).join(', ');
        return {
          value: group.name_en || group.code,
          label: codesLabel ? `${labelParts} (${codesLabel})` : labelParts,
          codes: group.codes || [],
          type: group.type,
        };
      }),
    [idnLanguageOptions],
  );

  const idnOptionMap = useMemo(() => {
    const map = {};
    idnOptions.forEach((opt) => {
      map[opt.value] = opt;
    });
    return map;
  }, [idnOptions]);

  const expandIdnSelection = (value) => {
    const values = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(/[\s,;]+/).filter((v) => v)
        : [];
    const expanded = [];
    values.forEach((item) => {
      const opt = idnOptionMap[item];
      if (opt && Array.isArray(opt.codes) && opt.codes.length) {
        expanded.push(...opt.codes);
      } else if (item) {
        expanded.push(item);
      }
    });
    return Array.from(
      new Set(
        expanded.map((code) => (typeof code === 'string' ? code.toUpperCase() : code)),
      ),
    );
  };

  useEffect(() => {
    fetchTldPricings();
    fetchRegistrars();
  }, []);

  // fetchTldPricings(filters, page?, pageSize?)：page 缺省时停留在当前页
  const fetchTldPricings = async (params = filters, page, pageSize) => {
    const current = typeof page === 'number' ? page : pagination.current;
    const size = pageSize || pagination.pageSize;
    setLoading(true);
    try {
      const response = await registrarService.getTldPricings({
        ...params,
        page: current,
        page_size: size,
      });
      if (response.success) {
        setTldPricings(Array.isArray(response.data) ? response.data : []);
        setPagination({
          current: Number(response.page) || current,
          pageSize: Number(response.page_size) || size,
          total: Number(response.total) || 0,
        });
        if (response.meta?.idn_groups) {
          setIdnLanguageOptions(response.meta.idn_groups);
        }
      }
    } catch (error) {
      message.error('获取TLD价格列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrars = async () => {
    try {
      const response = await registrarService.getRegistrars();
      if (response.success) {
        setRegistrars(response.data);
      }
    } catch (error) {
      message.error('获取注册商列表失败');
    }
  };

  const handleEdit = (tld) => {
    setEditingTld(tld);
    const toNumber = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

    form.setFieldsValue({
      markup_percentage: toNumber(tld.markup_percentage),
      markup_fixed: toNumber(tld.markup_fixed),
      currency: tld.currency,
      enabled: tld.is_available,
      tld_unicode: tld.tld_unicode || tld.tld,
      is_idn: tld.is_idn,
      is_dnssec: tld.is_dnssec,
      is_privacy: tld.is_privacy,
      idn_scripts: tld.idn_scripts_list || [],
      registration_price: toNumber(tld.registration_price),
      renewal_price: toNumber(tld.renewal_price),
      transfer_price: toNumber(tld.transfer_price),
      redemption_price: toNumber(tld.redemption_price),
    });
    setModalVisible(true);
  };

  const handleViewMeta = async (record) => {
    setMetaVisible(true);
    setMetaContent('加载中...');
    try {
      const resp = await registrarService.getTldMeta(record.registrar_id, record.tld);
      if (resp.success) {
        setMetaContent(JSON.stringify(resp.data, null, 2));
      } else {
        setMetaContent(`错误: ${resp.error || '未知错误'}`);
      }
    } catch (error) {
      setMetaContent(`错误: ${error.message || '请求失败'}`);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        id: editingTld.id,
        // 售价 = 成本 + max(成本×比例, 最低利润)；留空 = 全局默认
        markup_percentage: values.markup_percentage ?? null,
        markup_fixed: values.markup_fixed ?? null,
        currency: values.currency,
        enabled: values.enabled,
        tld_unicode: values.tld_unicode,
        is_idn: values.is_idn,
        is_dnssec: values.is_dnssec,
        is_privacy: values.is_privacy,
        idn_scripts: expandIdnSelection(values.idn_scripts),
      };

      setLoading(true);

      if (editingTld.pricing_mode === 'manual') {
        try {
          const mp = await registrarService.setManualPrices({
            id: editingTld.id,
            registration_price: values.registration_price,
            renewal_price: values.renewal_price,
            transfer_price: values.transfer_price ?? 0,
            redemption_price: values.redemption_price ?? null,
          });
          if (!mp.success) {
            message.error(mp.error || '保存售价失败');
            return;
          }
        } catch (e) {
          message.error(e.error || e.message || '保存售价失败');
          return;
        }
      }
      if (editingTld.loss_guard_tripped_at) {
        delete data.enabled; // 亏本下架的行只能用「重新上架」恢复
      }

      const response = await registrarService.updateTldPricing(data);

      if (response.success) {
        message.success('更新TLD价格成功');
        setModalVisible(false);
        fetchTldPricings(filters);
      } else {
        message.error(response.error || '更新TLD价格失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = (tld, rows) => {
    const headers = ['tld', 'registrar', 'price', 'currency', 'before', 'after', 'changed'];
    const lines = [headers.join(',')];
    rows.forEach((row) => {
      const cols = [
        row.tld,
        row.registrar || '',
        row.registration_price,
        row.currency || '',
        row.before,
        row.after,
        row.changed ? '是' : '否',
      ];
      lines.push(cols.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tld-exclusive-${tld}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const submitForceExclusive = async ({ record, tlds, preferredIds }) => {
    try {
      if (record) {
        setExclusiveLoadingId(record.id);
      } else {
        setBulkLoading(true);
      }
      const payload = record
        ? {
            id: record.id,
            tld: record.tld,
            preferred_registrar_ids: preferredIds?.length ? preferredIds : [record.registrar_id],
            priority: priorityRules,
          }
        : {
            tlds,
            preferred_registrar_ids: preferredIds,
            priority: priorityRules,
          };

      const request = record
        ? registrarService.forceExclusiveTld(payload)
        : registrarService.forceExclusiveTldBulk(payload);

      const response = await request;
      if (response.success) {
        const rows = response.data?.rows || response.data?.rows || [];
        const tld = record ? record.tld : 'bulk';
        message.success('已按规则启用最低价注册商');
        if (rows && rows.length > 0) {
          exportCsvRows(rows, `tld-exclusive-${tld}`);
        }
        fetchTldPricings(filters);
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (err) {
      message.error(err?.error || '操作失败');
    } finally {
      setExclusiveLoadingId(null);
      setBulkLoading(false);
      setExclusiveModalOpen(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const normalizedValue =
      Array.isArray(value) && value.length === 0
        ? undefined
        : value;
    const newFilters = { ...filters, [key]: normalizedValue };
    if (normalizedValue === undefined && normalizedValue !== false) {
      delete newFilters[key];
    }
    setFilters(newFilters);
    fetchTldPricings(newFilters, 1);
  };

  // 当前筛选下的全部去重 TLD（服务端计算，不拉全量行）
  const fetchFilteredTlds = async () => {
    const response = await registrarService.getTldPricings({ ...filters, tlds_only: 1 });
    return response.success && Array.isArray(response.data) ? response.data : [];
  };

  const exportCsvRows = (rows, filenamePrefix = 'tld-exclusive-bulk') => {
    if (!rows || rows.length === 0) return;
    const headers = ['tld', 'registrar', 'price', 'currency', 'before', 'after', 'changed'];
    const lines = [headers.join(',')];
    rows.forEach((row) => {
      const cols = [
        row.tld,
        row.registrar || '',
        row.registration_price,
        row.currency || '',
        row.before,
        row.after,
        row.changed ? '是' : '否',
      ];
      lines.push(cols.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filenamePrefix}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleForceExclusiveBulk = async () => {
    if (pagination.total === 0) {
      message.warning('当前筛选无数据');
      return;
    }
    setExclusiveMode('bulk');
    setExclusiveRecord(null);
    setPreferredRegistrarIds([]);
    setExclusiveModalOpen(true);
  };

  // 加价百分比 = (price - cost) / cost * 100
  const computeMargin = (record) => {
    const cost = Number(record.cost_price);
    const price = Number(record.registration_price);
    if (!cost || cost <= 0 || !price) return null;
    return ((price - cost) / cost) * 100;
  };

  const setRowsMode = async (rows, mode) => {
    if (!rows.length) {
      message.warning('请先选择 TLD');
      return;
    }
    if (rows.length === 1) setModeLoadingId(rows[0].id);
    try {
      const r = await registrarService.setPricingMode(rows.map((x) => x.id), mode);
      if (r.success) {
        message.success(`已设为${mode === 'manual' ? '手动' : '自动'}定价（${r.data?.changed ?? 0} 行）`);
        fetchTldPricings(filters);
      } else {
        message.error(r.error || '操作失败');
      }
    } catch (e) {
      message.error(e.error || e.message || '操作失败');
    } finally {
      setModeLoadingId(null);
    }
  };

  const reenableRow = async (record) => {
    setReenableLoadingId(record.id);
    try {
      const r = await registrarService.reenableTld(record.id);
      if (r.success) {
        message.success(`.${record.tld} 已重新上架`);
        fetchTldPricings(filters);
      } else {
        message.error(r.error || '重新上架失败');
      }
    } catch (e) {
      message.error(e.error || e.message || '重新上架失败');
    } finally {
      setReenableLoadingId(null);
    }
  };

  // has_pricing 已改为服务端筛选
  const filteredTldPricings = tldPricings;

  const selectedTldRecords = useMemo(
    () => selectedTldRows.filter((t) => t && selectedTldRowKeys.includes(t.id)),
    [selectedTldRows, selectedTldRowKeys]
  );

  const clearTldSelection = () => {
    setSelectedTldRowKeys([]);
    setSelectedTldRows([]);
  };

  const applyBulkMarkup = async () => {
    if (selectedTldRecords.length === 0) {
      message.warning('请先选择 TLD');
      return;
    }
    if (bulkMarkupValue === null || bulkMarkupValue === undefined || isNaN(Number(bulkMarkupValue))) {
      message.warning('请输入加价百分比');
      return;
    }
    setBulkMarkupLoading(true);
    let ok = 0, fail = 0;
    try {
      for (const rec of selectedTldRecords) {
        try {
          // 设置该后缀的加价比例覆盖；售价由后端按成本重算
          const r = await registrarService.updateTldPricing({
            id: rec.id,
            markup_percentage: Number(bulkMarkupValue),
            currency: rec.currency,
            enabled: rec.is_available,
            tld_unicode: rec.tld_unicode || rec.tld,
            is_idn: rec.is_idn,
            is_dnssec: rec.is_dnssec,
            is_privacy: rec.is_privacy,
            idn_scripts: rec.idn_scripts_list || [],
          });
          if (r.success || r.code === 0) ok++; else fail++;
        } catch (e) {
          fail++;
        }
      }
      message.success(`批量加价完成 — 成功 ${ok}, 失败 ${fail}`);
      setBulkMarkupOpen(false);
      clearTldSelection();
      fetchTldPricings(filters);
    } finally {
      setBulkMarkupLoading(false);
    }
  };

  const applyBulkEnabled = async (enabled) => {
    if (selectedTldRecords.length === 0) {
      message.warning('请先选择 TLD');
      return;
    }
    setBulkEnableLoading(true);
    let ok = 0, fail = 0;
    try {
      for (const rec of selectedTldRecords) {
        try {
          const r = await registrarService.updateTldPricing({
            id: rec.id,
            currency: rec.currency,
            enabled,
            tld_unicode: rec.tld_unicode || rec.tld,
            is_idn: rec.is_idn,
            is_dnssec: rec.is_dnssec,
            is_privacy: rec.is_privacy,
            idn_scripts: rec.idn_scripts_list || [],
          });
          if (r.success || r.code === 0) ok++; else fail++;
        } catch (e) {
          fail++;
        }
      }
      message.success(`批量${enabled ? '启用' : '禁用'}完成 — 成功 ${ok}, 失败 ${fail}`);
      clearTldSelection();
      fetchTldPricings(filters);
    } finally {
      setBulkEnableLoading(false);
    }
  };

  // 隐私模式（代理注册）支持：NULL=默认支持；关 = 0
  const setPrivacyRows = async (rows, on) => {
    if (!rows.length) {
      message.warning('请先选择 TLD');
      return;
    }
    try {
      const r = await registrarService.setPrivacyRowSupport({ ids: rows.map((x) => x.id), supported: on ? null : 0 });
      if (r.success) {
        message.success(`隐私模式已${on ? '开启' : '关闭'}（${r.data?.updated ?? 0} 行）`);
        setTldPricings((prev) => prev.map((x) => (rows.some((y) => y.id === x.id) ? { ...x, privacy_mode_supported: on ? null : 0 } : x)));
      } else {
        message.error(r.error || '操作失败');
      }
    } catch (e) {
      message.error(e.error || e.message || '操作失败');
    }
  };

  const exportTldCsv = async () => {
    // 按当前筛选分批拉取全部行（每批 500），避免一次性大响应
    setExporting(true);
    const rows = [];
    try {
      for (let page = 1; page <= 100; page++) {
        const resp = await registrarService.getTldPricings({ ...filters, page, page_size: 500 });
        const batch = resp.success && Array.isArray(resp.data) ? resp.data : [];
        rows.push(...batch);
        if (batch.length < 500 || rows.length >= (Number(resp.total) || 0)) break;
      }
    } catch (e) {
      message.error('导出失败：获取数据出错');
      setExporting(false);
      return;
    }
    setExporting(false);
    if (rows.length === 0) {
      message.warning('当前筛选无可导出数据');
      return;
    }
    const headers = ['id', 'tld', 'registrar', 'currency', 'cost_price',
      'registration_price', 'renewal_price', 'transfer_price',
      'markup_percentage', 'computed_margin_%', 'is_available', 'is_idn', 'is_dnssec'];
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      const computed = computeMargin(r);
      const cols = [
        r.id,
        r.tld,
        r.registrar_name || '',
        r.currency || '',
        r.cost_price || '',
        r.registration_price || '',
        r.renewal_price || '',
        r.transfer_price || '',
        r.markup_percentage || '',
        computed !== null ? computed.toFixed(2) : '',
        r.is_available ? '是' : '否',
        r.is_idn ? '是' : '否',
        r.is_dnssec ? '是' : '否',
      ];
      lines.push(cols.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tld-pricing-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success(`已导出 ${rows.length} 条 TLD 价格`);
  };

  const columns = [
    {
      title: 'TLD',
      dataIndex: 'tld',
      key: 'tld',
      render: (tld, record) => (
        <Space>
          <Tag color="blue">{tld}</Tag>
          {record.tld_unicode && record.tld_unicode !== tld ? (
            <Tag color="purple">{record.tld_unicode}</Tag>
          ) : null}
          {record.is_idn ? <Tag color="purple">IDN</Tag> : null}
          {record.is_dnssec === false ? <Tag>DNSSEC未支持</Tag> : <Tag color="blue">DNSSEC</Tag>}
        </Space>
      ),
      sorter: (a, b) => a.tld.localeCompare(b.tld),
    },
    {
      title: '注册商',
      dataIndex: 'registrar_name',
      key: 'registrar_name',
      render: (name, record) => (
        <Space>
          {name}
          {record.registrar_enabled ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              启用
            </Tag>
          ) : (
            <Tag color="default" icon={<CloseCircleOutlined />}>
              禁用
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '注册价格',
      dataIndex: 'registration_price',
      key: 'registration_price',
      render: (price, record) => `${record.currency} ${price}`,
      sorter: (a, b) => a.registration_price - b.registration_price,
    },
    {
      title: '续费价格',
      dataIndex: 'renewal_price',
      key: 'renewal_price',
      render: (price, record) => `${record.currency} ${price}`,
      sorter: (a, b) => a.renewal_price - b.renewal_price,
    },
    {
      title: '转入价格',
      dataIndex: 'transfer_price',
      key: 'transfer_price',
      render: (price, record) =>
        price ? `${record.currency} ${price}` : '-',
    },
    {
      title: '成本价',
      dataIndex: 'cost_price',
      key: 'cost_price',
      render: (price, record) =>
        price ? `${record.currency} ${price}` : '-',
    },
    {
      title: <span title="自动 = 成本 + 加价，每日同步更新；手动 = 固定售价，同步只改成本">模式</span>,
      dataIndex: 'pricing_mode',
      key: 'pricing_mode',
      width: 84,
      render: (mode, record) => (
        <Switch
          size="small"
          checkedChildren="手动"
          unCheckedChildren="自动"
          checked={mode === 'manual'}
          loading={modeLoadingId === record.id}
          onChange={(on) => setRowsMode([record], on ? 'manual' : 'auto')}
        />
      ),
    },
    {
      title: '加价',
      dataIndex: 'markup_percentage',
      key: 'markup_percentage',
      render: (markup, record) => (
        record.pricing_mode === 'manual'
          ? <Text type="secondary">-</Text>
          : markup === null && record.markup_fixed === null
          ? <Text type="secondary">默认</Text>
          : `${markup ?? '默认'}%${record.markup_fixed !== null ? ` / ≥${record.markup_fixed}` : ''}`
      ),
    },
    {
      title: '实际毛利',
      key: 'computed_margin',
      width: 100,
      sorter: (a, b) => (computeMargin(a) || 0) - (computeMargin(b) || 0),
      render: (_, record) => {
        const m = computeMargin(record);
        if (m === null) return <Text type="secondary">-</Text>;
        const color = m < 0 ? 'red' : m < 10 ? 'orange' : m < 30 ? 'gold' : 'green';
        return <Tag color={color}>{m.toFixed(1)}%</Tag>;
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: '状态',
      dataIndex: 'is_available',
      key: 'is_available',
      render: (is_available, record) =>
        record.loss_guard_tripped_at ? (
          <Space size={4} wrap>
            <Tooltip title={`${record.loss_guard_reason || ''}（${record.loss_guard_tripped_at}）`}>
              <Tag color="red" icon={<StopOutlined />}>亏本已下架</Tag>
            </Tooltip>
            <Button type="link" size="small" style={{ padding: 0 }} loading={reenableLoadingId === record.id} onClick={() => reenableRow(record)}>
              重新上架
            </Button>
          </Space>
        ) : is_available ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            可用
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            不可用
          </Tag>
        ),
    },
    {
      title: '隐私保护',
      dataIndex: 'is_privacy',
      key: 'is_privacy',
      render: (val) =>
        val ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            支持
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            不支持
          </Tag>
        ),
    },
    {
      title: <span title="隐私模式（代理注册）：默认支持；只有注册局禁止代理登记时才关">隐私模式</span>,
      dataIndex: 'privacy_mode_supported',
      key: 'privacy_mode_supported',
      width: 96,
      render: (val, record) => (
        <Switch
          size="small"
          checked={val === null || val === undefined || Number(val) !== 0}
          onChange={(on) => setPrivacyRows([record], on)}
        />
      ),
    },
    {
      title: 'IDN脚本',
      dataIndex: 'idn_scripts',
      key: 'idn_scripts',
      render: (_, record) =>
        record.idn_scripts_list && record.idn_scripts_list.length > 0
          ? record.idn_scripts_list.join(', ')
          : '-',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleViewMeta(record)}
          >
            获取详情
          </Button>
          <Button
            type="link"
            size="small"
            loading={exclusiveLoadingId === record.id}
            onClick={() => {
              setExclusiveMode('single');
              setExclusiveRecord(record);
              setPreferredRegistrarIds([record.registrar_id]);
              setExclusiveModalOpen(true);
            }}
          >
            仅启用此注册商
          </Button>
          <Button type="link" size="small" onClick={() => setGiftRow(record)}>
            赠品
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        icon={<CrownOutlined />}
        banner
        message="管理员视图 — 修改 TLD 价格会立刻影响所有用户的新增 / 续费报价"
      />

      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Title level={3} style={{ margin: 0 }}>TLD价格管理</Title>
        <Space wrap size={[8, 8]}>
          <Button icon={<DownloadOutlined />} onClick={exportTldCsv} loading={exporting} size="small" data-testid="tld-export-csv">
            导出CSV
          </Button>
          <Button loading={bulkLoading} onClick={handleForceExclusiveBulk} size="small">
            全部仅启用一个
          </Button>
          <Select
            placeholder="筛选注册商"
            style={{ width: 180 }}
            allowClear
            onChange={(value) => handleFilterChange('registrar_id', value)}
          >
            {registrars.map((registrar) => (
              <Select.Option key={registrar.id} value={registrar.id}>
                {registrar.name}
              </Select.Option>
            ))}
          </Select>

          <Input.Search
            placeholder="搜索TLD"
            style={{ width: 180 }}
            allowClear
            onChange={(e) => {
              if (!e.target.value) handleFilterChange('search', undefined);
            }}
            onSearch={(value) => handleFilterChange('search', (value || '').trim() || undefined)}
          />

          <Select
            placeholder="状态"
            style={{ width: 120 }}
            allowClear
            onChange={(value) => handleFilterChange('enabled', value)}
          >
            <Select.Option value={true}>可用</Select.Option>
            <Select.Option value={false}>不可用</Select.Option>
          </Select>

          <Select
            placeholder="是否 IDN"
            style={{ width: 120 }}
            allowClear
            value={idnFilter}
            onChange={(value) => {
              setIdnFilter(value);
              handleFilterChange('is_idn', value);
            }}
          >
            <Select.Option value={true}>是</Select.Option>
            <Select.Option value={false}>否</Select.Option>
          </Select>

          <Select
            placeholder="IDN脚本"
            style={{ width: 140 }}
            allowClear
            value={idnScriptFilter}
            onChange={(value) => {
              setIdnScriptFilter(value);
              handleFilterChange('idn_script', expandIdnSelection(value));
            }}
            options={idnOptions}
          />

          <Select
            placeholder="是否多服务商启用"
            style={{ width: 160 }}
            allowClear
            value={multiEnabledFilter}
            onChange={(value) => {
              setMultiEnabledFilter(value);
              handleFilterChange('multi_enabled', value);
            }}
          >
            <Select.Option value={true}>是</Select.Option>
            <Select.Option value={false}>否</Select.Option>
          </Select>

          <Select
            placeholder="是否已定价"
            style={{ width: 140 }}
            allowClear
            value={hasPricingFilter}
            onChange={(value) => {
              setHasPricingFilter(value);
              handleFilterChange('has_pricing', value);
            }}
          >
            <Select.Option value="has">已定价</Select.Option>
            <Select.Option value="none">未定价</Select.Option>
          </Select>

          <Select
            placeholder="定价模式"
            style={{ width: 120 }}
            allowClear
            onChange={(value) => handleFilterChange('pricing_mode', value)}
          >
            <Select.Option value="auto">自动</Select.Option>
            <Select.Option value="manual">手动</Select.Option>
          </Select>

          <Select
            placeholder="亏本下架"
            style={{ width: 120 }}
            allowClear
            onChange={(value) => handleFilterChange('loss_guard', value)}
          >
            <Select.Option value="tripped">亏本已下架</Select.Option>
          </Select>
        </Space>
      </div>

      {selectedTldRowKeys.length > 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message={
            <Space wrap>
              <span>已选择 {selectedTldRowKeys.length} 个 TLD：</span>
              <Button
                size="small"
                icon={<PercentageOutlined />}
                onClick={() => setBulkMarkupOpen(true)}
                data-testid="bulk-markup-btn"
              >
                批量加价
              </Button>
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                loading={bulkEnableLoading}
                onClick={() => applyBulkEnabled(true)}
              >
                批量启用
              </Button>
              <Button
                size="small"
                icon={<StopOutlined />}
                danger
                loading={bulkEnableLoading}
                onClick={() => applyBulkEnabled(false)}
              >
                批量禁用
              </Button>
              <Button size="small" onClick={() => setRowsMode(selectedTldRecords, 'manual')}>批量设为手动</Button>
              <Button size="small" onClick={() => setRowsMode(selectedTldRecords, 'auto')}>批量设为自动</Button>
              <Button size="small" onClick={() => setPrivacyRows(selectedTldRecords, true)}>批量开隐私模式</Button>
              <Button size="small" onClick={() => setPrivacyRows(selectedTldRecords, false)}>批量关隐私模式</Button>
              <Button size="small" onClick={clearTldSelection}>清除选择</Button>
            </Space>
          }
        />
      )}

      <Table
        columns={columns}
        dataSource={filteredTldPricings}
        rowKey="id"
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectedTldRowKeys,
          preserveSelectedRowKeys: true,
          onChange: (keys, rows) => {
            setSelectedTldRowKeys(keys);
            // antd 对跨页已选行可能给 undefined，用旧缓存补齐
            setSelectedTldRows((prev) => {
              const byId = new Map(prev.filter(Boolean).map((r) => [r.id, r]));
              (rows || []).filter(Boolean).forEach((r) => byId.set(r.id, r));
              return keys.map((k) => byId.get(k)).filter(Boolean);
            });
          },
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200', '500'],
          showTotal: (total) => `共 ${total} 条记录`,
          onChange: (page, pageSize) => fetchTldPricings(filters, page, pageSize),
        }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title="仅启用一个注册商"
        open={exclusiveModalOpen}
        onCancel={() => setExclusiveModalOpen(false)}
        onOk={() => {
          if (exclusiveMode === 'single' && exclusiveRecord) {
            submitForceExclusive({
              record: exclusiveRecord,
              preferredIds: preferredRegistrarIds,
            });
          } else if (exclusiveMode === 'bulk') {
            setBulkLoading(true);
            fetchFilteredTlds()
              .then((tlds) => {
                if (tlds.length === 0) {
                  setBulkLoading(false);
                  message.warning('当前筛选无数据');
                  return;
                }
                submitForceExclusive({
                  record: null,
                  tlds,
                  preferredIds: preferredRegistrarIds,
                });
              })
              .catch(() => {
                setBulkLoading(false);
                message.error('获取当前筛选的 TLD 列表失败');
              });
          }
        }}
        okButtonProps={{ loading: bulkLoading || (exclusiveRecord && exclusiveLoadingId === exclusiveRecord.id) }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            将对选定 TLD 仅启用 1 个注册商，规则：
            <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
              <li>自定义优先级（可拖动/点击调整顺序）</li>
              <li>默认：优先注册商 &gt; 价格最低 &gt; DNSSEC &gt; 隐私</li>
            </ul>
          </div>
          <Select
            mode="multiple"
            allowClear
            placeholder="选择并排列优先级（点击顺序即优先顺序）"
            value={priorityRules}
            onChange={(vals) => {
              if (!vals || vals.length === 0) {
                setPriorityRules(['preferred_registrar', 'price_lowest', 'dnssec', 'privacy']);
              } else {
                setPriorityRules(vals);
              }
            }}
            style={{ width: '100%' }}
            options={[
              { label: '优先注册商', value: 'preferred_registrar' },
              { label: '价格最低', value: 'price_lowest' },
              { label: '支持DNSSEC', value: 'dnssec' },
              { label: '支持隐私保护', value: 'privacy' },
            ]}
            size="middle"
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="优先注册商（可选，按顺序）"
            value={preferredRegistrarIds}
            onChange={setPreferredRegistrarIds}
            style={{ width: '100%' }}
            options={registrars.map((r) => ({
              label: r.name,
              value: r.id,
            }))}
            size="middle"
          />
          {exclusiveMode === 'bulk' ? (
            <div style={{ color: '#888' }}>作用范围：当前筛选结果中的全部 TLD</div>
          ) : (
            <div style={{ color: '#888' }}>
              目标 TLD：{exclusiveRecord?.tld}（当前注册商：{exclusiveRecord?.registrar_name}）
            </div>
          )}
        </Space>
      </Modal>

      <Modal
        title="编辑TLD价格"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="TLD">
            <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>
              {editingTld?.tld}
            </Tag>
          </Form.Item>

          <Form.Item label="注册商">
            <Tag style={{ fontSize: 14, padding: '4px 12px' }}>
              {editingTld?.registrar_name}
            </Tag>
          </Form.Item>

          <Form.Item label="Unicode显示" name="tld_unicode">
            <Input
              placeholder="如需要显示IDN，请填写unicode形式"
              disabled={!isIdn}
            />
          </Form.Item>

          <Form.Item label="IDN后缀" name="is_idn" valuePropName="checked">
            <Switch />
          </Form.Item>

          {isIdn ? (
            <Form.Item
              label="IDN脚本"
              name="idn_scripts"
              tooltip="选择支持的语言脚本，只有 IDN 后缀可编辑，可直接输入自定义代码"
            >
              <Select
                mode="tags"
                allowClear
                placeholder="选择或输入语言脚本"
                options={idnOptions}
                onChange={(vals) => {
                  const expanded = expandIdnSelection(vals);
                  form.setFieldsValue({ idn_scripts: expanded });
                }}
              />
            </Form.Item>
          ) : null}

          <Form.Item label="支持DNSSEC" name="is_dnssec" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="支持隐私保护" name="is_privacy" valuePropName="checked">
            <Switch />
          </Form.Item>

          {editingTld && (
            <Form.Item label="成本 / 当前售价">
              <Text type="secondary" style={{ fontSize: 13 }}>
                注册 {editingTld.cost_price ?? '-'} / {editingTld.registration_price} · 续费 {editingTld.renewal_cost ?? '-'} / {editingTld.renewal_price} · 转入 {editingTld.transfer_cost ?? '-'} / {editingTld.transfer_price} {editingTld.currency}
              </Text>
            </Form.Item>
          )}

          {editingTld?.pricing_mode === 'manual' && (
            <Space wrap size={[12, 0]}>
              <Form.Item label="注册售价" name="registration_price" rules={[{ required: true, message: '必填' }]}>
                <InputNumber min={0} precision={2} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label="续费售价" name="renewal_price" rules={[{ required: true, message: '必填' }]}>
                <InputNumber min={0} precision={2} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label="转入售价" name="transfer_price" tooltip="0 = 不开放转入">
                <InputNumber min={0} precision={2} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label="赎回售价" name="redemption_price" tooltip="留空 = 不开放自助赎回">
                <InputNumber min={0} precision={2} style={{ width: 120 }} />
              </Form.Item>
            </Space>
          )}

          <Form.Item label="加价比例 (%)" name="markup_percentage" tooltip={editingTld?.pricing_mode === 'manual' ? '手动定价下不生效，切回自动时使用' : '留空使用全局默认（域名定价页）'}>
            <InputNumber style={{ width: '100%' }} min={0} max={500} precision={2} placeholder="默认" />
          </Form.Item>

          <Form.Item label="每年最低利润" name="markup_fixed" tooltip="留空使用全局默认；售价 = 成本 + max(成本×比例, 最低利润)">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="默认" />
          </Form.Item>

          <Form.Item
            label="货币"
            name="currency"
            rules={[{ required: true, message: '请选择货币' }]}
          >
            <Select placeholder="选择货币">
              <Select.Option value="CNY">CNY - 人民币</Select.Option>
              <Select.Option value="USD">USD - 美元</Select.Option>
              <Select.Option value="EUR">EUR - 欧元</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="可用" name="enabled" valuePropName="checked" tooltip={editingTld?.loss_guard_tripped_at ? '亏本已下架，请在列表中「重新上架」' : undefined}>
            <Switch disabled={!!editingTld?.loss_guard_tripped_at} />
          </Form.Item>
        </Form>
      </Modal>

      <AntdModal
        title="TLD 元数据"
        open={metaVisible}
        onCancel={() => setMetaVisible(false)}
        footer={null}
        width={800}
      >
        <pre style={{ maxHeight: 500, overflow: 'auto' }}>
{metaContent}
        </pre>
      </AntdModal>

      <Modal
        title="批量加价"
        open={bulkMarkupOpen}
        onCancel={() => setBulkMarkupOpen(false)}
        onOk={applyBulkMarkup}
        confirmLoading={bulkMarkupLoading}
        okText="应用"
        cancelText="取消"
        okButtonProps={{ 'data-testid': 'bulk-markup-apply-btn' }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`将对 ${selectedTldRowKeys.length} 个 TLD 按成本价 × (1 + 加价%) 计算新的注册价和续费价`}
          description="无 cost_price 的行会被跳过；转入价不会改动。"
        />
        <InputNumber
          value={bulkMarkupValue}
          onChange={setBulkMarkupValue}
          min={-50}
          max={500}
          precision={2}
          style={{ width: '100%' }}
          addonBefore="加价"
          addonAfter="%"
        />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">
            示例：cost=$10, 加价 20% → 新价 $12.00
          </Text>
        </div>
      </Modal>

      <GiftRulesModal
        open={!!giftRow}
        scope="tld_pricing"
        refId={giftRow?.id}
        title={giftRow ? `注册赠品 · .${giftRow.tld}${giftRow.registrar_name ? ` · ${giftRow.registrar_name}` : ''}` : ''}
        onClose={() => setGiftRow(null)}
      />
    </div>
  );
}
